import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../../state/worldStore';
import { useCityStore } from '../../city/cityStore';
import { stateAt } from '../../city/cityMotion';
import { serverNow } from '../../services/worldSocket';
import { useCutscene, cutsceneBridge } from '../../city/cutscene';
import { planShot, shotAt, shotKindFor, chooseSide, planStageShot, stageShotFor } from '../../city/sceneShots';
import { baseGroundY } from '../../city/cityGround';
import { songCuts } from '../../city/songAnalysis';
import { lastAtOrBefore, hitEnv } from '../../city/stageCuts';
import { songPosition, micWordsAt } from '../../city/stageBridge';
import { replayDone } from '../../city/replay';
import { bodyAt } from '../../city/bodies';
import { roadMapBridge } from '../../render/roadMapBridge';

// CityCutscene — the scene takes the camera, then gives it back.
//
// Khari, 2026-10-23: *"If you step into the artist's daily story, there will be
// portions where your control is disabled while the narrated scene takes over..
// when the view goes back to yours, you can follow along"*, and for the
// artists' street conversations, *"this would be great for listening to
// conversations as well."* See city/cutscene for the shape.
//
// TWO KINDS OF SCENE, ONE DIRECTOR. A street conversation (two artists, until
// it ends) and a City Story (an artist's episode: scenes at places, travel
// between them). Each resolves, every frame, to: who is in the scene now, the
// lines being said, and whether they are standing (a CUT: the scene has the
// camera) or on the move (FOLLOW: yours, with the way to the lead).
//
// ONE CLAIMANT AT A TIME (PlanetScene's rule for the ride and the car): while
// this holds the camera, `cutsceneBridge.active` makes WalkControls stand down.
// Your view is saved when the scene takes it and put back EXACTLY when it gives
// it back, so you are where you were standing — never teleported to wherever
// the last shot happened to be.
//
// Shots: city/sceneShots — an establishing view at each scene, then close-up ·
// over-the-shoulder · pan · two-shot · orbit by the speaker's turn (alone:
// close · medium · orbit · drift), all on one side of the line. Each shot is a
// MOVE, planned when it starts and played out on the city clock.

const CUE_RANGE = 14;          // the cue is offered this close (~30 m; never further: no remote alerts)
const ESTABLISH_MS = 3800;
// A replay's first shot is GLIDED into from wherever the camera was — the
// fly-in lands on that shot's opening frame (city/replay replayLanding), so it
// is a hair's breadth; anything the landing could not know (a spot snapped
// differently once the road map arrived) is smoothed instead of cut.
const GLIDE_MS = 1200;
// You have to follow them to see what happens next (Khari: "you have to follow
// them if you want to see what happens next in the story"): a story's scene
// only takes the camera for someone this close to it.
const STORY_NEAR = 32;
// The day's chapters (API CityStoryService.CHAPTERS), as the cue says them.
const CHAPTER = { morning: 'This morning ·', midday: 'Midday ·', evening: 'Tonight ·' };
// A set on the plaza stage is offered anywhere you can hear it well (CityStage
// FAR is where the music is gone), from a minute before it starts.
const STAGE_CUE_RANGE = 22;
const STAGE_EARLY_MS = 60000;
const showKey = (sh) => `${sh.a}:${sh.t0}`;
// A set's live coverage (city/stageCuts): the cameras an operator holds feel the
// kick; a whip pan turns this far (rad) in its last and first 0.13 s, smeared by
// up to this blur (px) at its fastest.
const HANDHELD = new Set(['pit', 'wing', 'crowd', 'close', 'front']);
const WHIP = 0.9;
const WHIP_S = 0.13;
const BLUR = 6;
const easeOutExpo = (u) => (u >= 1 ? 1 : 1 - Math.pow(2, -10 * Math.max(0, u)));
/** Where a set's stage is: { x, z, facing, floor }, or null before the places arrive. */
function stageOf(sh, cs) {
  // A set inside a venue has no stage out here to film (VenueStage plays it in its room).
  if (sh.inside) return null;
  const pl = (cs.snap?.places || []).find((p) => p.slug === (sh.place || 'street-plaza'));
  const at = pl?.stage?.at;
  if (!at) return null;
  return { x: at[0], z: at[1], facing: pl?.stage?.facing ?? 0, floor: baseGroundY(at[0], at[1]) };
}

export default function CityCutscene() {
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  // A whip pan's blur, wanted by this frame's shot and put on the canvas after it.
  const blurWant = useRef(0);
  const blurNow = useRef(0);
  const setBlur = (px) => {
    const v = Math.round(px * 2) / 2;
    if (v === blurNow.current) return;
    blurNow.current = v;
    gl.domElement.style.filter = v > 0 ? `blur(${v}px)` : '';
  };
  // Unmounting lets go too (2026-10-29: an indoor scene switches into a room,
  // which unmounts this): a stuck `active` would keep the doors and the walker off.
  useEffect(() => () => {
    gl.domElement.style.filter = '';
    cutsceneBridge.active = false; cutsceneBridge.at = null; cutsceneBridge.scene = null;
  }, [gl]);
  const npcList = useWorldStore((s) => s.npcList);
  const heightOf = useMemo(() => {
    const m = new Map();
    for (const n of npcList || []) if (n?.avatarId != null) m.set(Number(n.avatarId), n.height || 0.75);
    return m;
  }, [npcList]);

  const saved = useRef(null);      // { pos, quat, fov } — the visitor's own view
  const scene = useRef(null);      // { key, startAt, side, shotKey, shotAt }
  const acc = useRef(0);
  const tmp = useMemo(() => ({ look: new THREE.Vector3(), fwd: new THREE.Vector3(), q: new THREE.Quaternion() }), []);
  const glide = useRef(null);      // { pos, quat, fov, at } — where a replay's first take glides from

  const person = (id, st) => ({
    id, x: st.x, z: st.z, facing: st.heading || 0,
    head: (st.y || 0) + (heightOf.get(Number(id)) || 0.75) * 0.92,
  });

  // Always lets go of the camera — even a scene that ended before it ever took
  // the view (a replay whose landing held the camera for it, then a long stall)
  // must not leave the walker standing down.
  const giveBack = () => {
    if (saved.current) {
      camera.position.copy(saved.current.pos);
      camera.quaternion.copy(saved.current.quat);
      if (camera.isPerspectiveCamera) { camera.fov = saved.current.fov; camera.updateProjectionMatrix(); }
    }
    saved.current = null;
    glide.current = null;
    setBlur(0);
    cutsceneBridge.active = false;
    cutsceneBridge.at = null;
    cutsceneBridge.scene = null;
  };

  /**
   * What a target is right now:
   *   { end } — it is over
   *   { follow: avatarId, name } — on the move
   *   { key, people: [{ id, st }], lines, names } — a scene, standing
   */
  const resolve = (target, cs, t, me) => {
    // WALKING WITH SOMEONE (Avva's "an invitation to walk with me"): follow the
    // artist into whatever they do next — their episode, a scene they are in,
    // a conversation — and stay with them in between, until they go home.
    if (target.kind === 'artist') {
      const id = Number(target.id);
      const r = cs.byId[id];
      const s = r && stateAt(r, t, cs.convs);
      if (!s) return { end: true };
      const story = Object.values(cs.stories || {}).find((x) => t >= x.t0 - 60000 && t <= x.t1 && (x.a === id || (x.scenes || []).some((sc) => sc.w === id && t >= sc.t0 - 60000 && t < sc.t1)));
      if (story) return resolve({ kind: 'story', id: story.id }, cs, t, me);
      // Up on the plaza stage: their set, in scene view, for as long as it lasts.
      const set = s.show && !s.inside && (cs.shows || []).find((x) => x.a === id && !x.inside && t >= x.t0 - STAGE_EARLY_MS && t < x.t1);
      if (set) return resolve({ kind: 'stage', id: showKey(set) }, cs, t, me);
      const conv = Object.values(cs.convs || {}).find((c) => c.status === 'live' && (c.a === id || c.b === id) && (c.lines || []).length);
      if (conv && Math.hypot(me.x - s.x, me.z - s.z) < STORY_NEAR) {
        const rc = resolve({ kind: 'conv', id: conv.id }, cs, t, me);
        if (!rc.end) return rc;
      }
      return { follow: id, name: r.name, st: s };
    }
    if (target.kind === 'stage') {
      // A SET ON THE PLAZA STAGE: the stage has the camera from stepping in to
      // the last song's end (a moment's grace), whoever is or is not up there.
      const sh = (cs.shows || []).find((x) => showKey(x) === target.id);
      const stage = sh && stageOf(sh, cs);
      if (!sh || !stage || t > sh.t1 + 2500) return { end: true };
      const rp = cs.byId[sh.a];
      const sp = rp && stateAt(rp, t, cs.convs);
      const up = !!(sp && sp.show && !sp.moving);
      return { key: `stage:${target.id}`, scene: { kind: 'stage', id: target.id }, stage, show: sh, performer: up ? { id: sh.a, st: sp } : null };
    }
    if (target.kind === 'conv') {
      const c = cs.convs?.[target.id];
      const lastEnd = Math.max(0, ...((c?.lines || []).map((l) => (l.playAt || 0) + (l.dur || 3) * 1000)));
      if (!c || (c.status !== 'live' && t > lastEnd + 2500)) return { end: true };
      const ra = cs.byId[c.a]; const rb = cs.byId[c.b];
      const sa = ra && stateAt(ra, t, cs.convs); const sb = rb && stateAt(rb, t, cs.convs);
      if (!sa || !sb) return { end: true };
      if (sa.moving || sb.moving) return { follow: c.a, name: ra.name, st: sa };
      return { key: c.id, scene: { kind: 'conv', id: c.id }, people: [{ id: c.a, st: sa }, { id: c.b, st: sb }], lines: c.lines || [], names: { [c.a]: ra.name, [c.b]: rb.name } };
    }
    const story = cs.stories?.[target.id];
    // A replay is cut to end on its last beat (city/replay); live, a moment's grace.
    if (!story || t > story.t1 + (cs.replay ? 0 : 3000)) return { end: true };
    const rl = cs.byId[story.a];
    const sl = rl && stateAt(rl, t, cs.convs);
    if (!sl) return t < story.t0 ? { follow: story.a, name: story.name, st: null } : { end: true };
    // A replay's first moments ARE its first scene (it starts out of the fly-in
    // landing on that scene's opening frame — city/replay).
    const sc = (story.scenes || []).find((x) => x.t0 <= t && t < x.t1)
      || (cs.replay && story.scenes?.length && t < story.scenes[0].t0 ? story.scenes[0] : null);
    if (!sc || sl.moving || sl.story !== story.id || sl.scene !== sc.i) return { follow: story.a, name: story.name, st: sl };
    // (A replay is on rails: the scene takes the camera wherever you are.)
    if (!cs.replay && Math.hypot(me.x - sl.x, me.z - sl.z) > STORY_NEAR) return { follow: story.a, name: story.name, st: sl };
    const people = [{ id: story.a, st: sl }];
    const names = { [story.a]: story.name };
    if (sc.w != null) {
      const rc = cs.byId[sc.w];
      const scs = rc && stateAt(rc, t, cs.convs);
      if (scs && !scs.moving && scs.story === story.id) { people.push({ id: sc.w, st: scs }); names[sc.w] = rc.name; }
    }
    return { key: `${story.id}:${sc.i}`, scene: { kind: 'story', id: story.id }, people, lines: (story.lines || []).filter((l) => l.scene === sc.i), names };
  };

  /** Ease the camera from where a replay's first take found it into the frame just set. */
  const glideIn = () => {
    const g = glide.current;
    if (!g) return;
    const k = (performance.now() - g.at) / GLIDE_MS;
    if (k >= 1) { glide.current = null; return; }
    const e = k * k * (3 - 2 * k);
    tmp.q.copy(camera.quaternion);
    camera.position.lerpVectors(g.pos, camera.position, e);
    camera.quaternion.slerpQuaternions(g.quat, tmp.q, e);
    if (camera.isPerspectiveCamera) { camera.fov = g.fov + (camera.fov - g.fov) * e; camera.updateProjectionMatrix(); }
  };

  useFrame((_, dt) => {
    blurWant.current = 0;
    const cs = useCityStore.getState();
    const ct = useCutscene.getState();
    const t = serverNow();

    // ── What is on offer here (a few times a second) ────────────────────────
    acc.current += dt;
    const slow = acc.current > 0.25;
    if (slow) {
      acc.current = 0;
      let cue = null;
      if (cs.live && !ct.target) {
        let best = Infinity;
        const consider = (d, c, range = CUE_RANGE, weight = 1) => {
          if (ct.dismissed.has(`${c.kind}:${c.id}`)) return;   // waved away (✕)
          if (d < range && d * weight < best) { best = d * weight; cue = c; }
        };
        // A set on the stage comes first: it is the square's main event.
        for (const sh of cs.shows || []) {
          if (t < sh.t0 - STAGE_EARLY_MS || t >= sh.t1 - 5000) continue;
          const stg = stageOf(sh, cs);
          if (!stg) continue;
          consider(Math.hypot(camera.position.x - stg.x, camera.position.z - stg.z),
            { kind: 'stage', id: showKey(sh), title: sh.name, sub: t < sh.t0 ? 'is about to play the plaza stage' : 'is live on the plaza stage', cta: 'Watch', full: `${sh.name} · Live on the plaza stage` },
            STAGE_CUE_RANGE, 0.3);
        }
        for (const c of Object.values(cs.convs || {})) {
          if (c.status !== 'live' || !(c.lines || []).length) continue;
          const ra = cs.byId[c.a]; const rb = cs.byId[c.b];
          const sa = ra && stateAt(ra, t, cs.convs); const sb = rb && stateAt(rb, t, cs.convs);
          if (!sa || !sb || sa.moving || sb.moving) continue;
          consider(Math.hypot(camera.position.x - (sa.x + sb.x) / 2, camera.position.z - (sa.z + sb.z) / 2),
            // A freestyle (CityFreestyleService) says so, and is a little more on offer than a chat.
            c.origin === 'freestyle'
              ? { kind: 'conv', id: c.id, title: `${ra.name} and ${rb.name}`, sub: 'are freestyling at the court', full: `Freestyle at the court · ${ra.name} vs ${rb.name}` }
              : { kind: 'conv', id: c.id, title: `${ra.name} and ${rb.name}`, sub: 'are talking' },
            c.origin === 'freestyle' ? 18 : CUE_RANGE, c.origin === 'freestyle' ? 0.6 : 1);
        }
        // A story is a little more on offer than a chat (it is someone's episode).
        for (const st of Object.values(cs.stories || {})) {
          if (t < st.t0 - 60000 || t > st.t1) continue;
          const rl = cs.byId[st.a]; const sl = rl && stateAt(rl, t, cs.convs);
          if (!sl) continue;
          consider(Math.hypot(camera.position.x - sl.x, camera.position.z - sl.z) * 0.8,
            { kind: 'story', id: st.id, title: st.name, sub: `${CHAPTER[st.chapter] || 'Episode'} ${st.ep} · ${st.title}`, full: `${st.name} · ${CHAPTER[st.chapter] || 'Episode'} ${st.ep}: ${st.title}` });
        }
      }
      if ((ct.cue?.id || null) !== (cue?.id || null)) ct.setCue(cue);
    }

    // ── Stepped in ──────────────────────────────────────────────────────────
    const target = ct.target;
    if (!target || !cs.live) {
      if (saved.current) giveBack();
      scene.current = null;
      if (target && !cs.live) ct.leave();
      return;
    }
    // Where the visitor really is: during a cut the camera is the scene's.
    const me = saved.current ? saved.current.pos : camera.position;
    const r = resolve(target, cs, t, me);
    if (r.end) { giveBack(); scene.current = null; ct.leave(); if (cs.replay) replayDone(); return; }

    // A REPLAY between scenes: no handing the view back — the camera rides
    // behind the lead (walking, or a car's length back when they drive), so the
    // episode plays straight through, view-only (city/replay).
    if (r.follow != null && cs.replay && r.st) {
      if (!saved.current) {
        saved.current = { pos: camera.position.clone(), quat: camera.quaternion.clone(), fov: camera.fov };
        cutsceneBridge.active = true;
        cutsceneBridge.at = saved.current.pos;
        glide.current = { ...saved.current, at: performance.now() };
      }
      cutsceneBridge.scene = target.kind === 'story' ? { kind: 'story', id: target.id } : null;
      if (ct.phase !== 'cut') ct.setPhase('cut');
      if (ct.caption) ct.setCaption(null);
      // The body as DRAWN (city/bodies), which trails the timeline at a car's speed.
      const b = bodyAt.get(Number(r.follow));
      const src = b && performance.now() - b.at < 500 ? b : r.st;
      const back = r.st.driving ? 4.2 : 2.6;
      const up = r.st.driving ? 1.7 : 1.15;
      // A CUT in the trip (city/replay): the timeline jumps, and so does the camera
      // — framed on where they now are (the drawn body catches up a frame late).
      const cutSeg = r.st.seg?.cut ? r.st.seg : null;
      const chaseKey = cutSeg ? `chase:${cutSeg.t0}` : 'chase';
      const fresh = scene.current?.key !== chaseKey;
      const at = fresh && cutSeg ? r.st : src;
      const ah = at.heading || 0;
      const want = new THREE.Vector3(at.x - Math.sin(ah) * back, (at.y || 0) + up, at.z - Math.cos(ah) * back);
      if (fresh) { scene.current = { key: chaseKey, snapUntil: performance.now() + 250 }; camera.position.copy(want); }
      else if (performance.now() < (scene.current.snapUntil || 0)) camera.position.copy(want);
      else camera.position.lerp(want, 1 - Math.exp(-dt * 6));
      tmp.look.set(at.x, (at.y || 0) + 0.55, at.z);
      camera.lookAt(tmp.look);
      if (camera.isPerspectiveCamera && Math.abs(camera.fov - 50) > 0.01) { camera.fov = 50; camera.updateProjectionMatrix(); }
      glideIn();
      if (import.meta.env.DEV) cutsceneBridge.shot = { kind: 'chase', pos: camera.position.toArray(), look: tmp.look.toArray(), fov: 50, aspect: camera.aspect, people: [{ id: r.follow, x: src.x, z: src.z, head: (src.y || 0) + 0.7, driving: !!r.st.driving, moving: !!r.st.moving }] };
      return;
    }

    if (r.follow != null) {
      // Yours again: follow along. The chip points the way to them.
      if (saved.current) giveBack();
      if (ct.phase !== 'follow') { ct.setPhase('follow'); ct.setCaption(null); }
      if (ct.followName !== r.name) ct.setFollowName(r.name);
      if (slow && r.st) {
        camera.getWorldDirection(tmp.fwd);
        const want = Math.atan2(r.st.x - camera.position.x, r.st.z - camera.position.z);
        const facing = Math.atan2(tmp.fwd.x, tmp.fwd.z);
        let rel = want - facing; while (rel > Math.PI) rel -= 2 * Math.PI; while (rel < -Math.PI) rel += 2 * Math.PI;
        ct.setFollowWay({ deg: Math.round((-rel * 180) / Math.PI), dist: Math.round(Math.hypot(r.st.x - camera.position.x, r.st.z - camera.position.z)) });
      }
      return;
    }

    // A SET: the stage has the camera, cut to the music (city/sceneShots stage shots).
    if (r.stage) {
      if (!saved.current) {
        saved.current = { pos: camera.position.clone(), quat: camera.quaternion.clone(), fov: camera.fov };
        cutsceneBridge.active = true;
        cutsceneBridge.at = saved.current.pos;
        // Into scene view with a glide from where you stand, not a jump.
        glide.current = { ...saved.current, at: performance.now() };
      }
      cutsceneBridge.scene = r.scene;
      if (ct.phase !== 'cut') ct.setPhase('cut');
      const sh = r.show;
      const P = r.performer ? person(r.performer.id, r.performer.st) : null;
      if (!scene.current || scene.current.key !== r.key) {
        scene.current = { key: r.key, side: Number(sh.a) % 2 ? -1 : 1, shotKey: null };
      }
      const sc = scene.current;
      const pick = stageShotFor(sh, t, !!P);
      const aspectKey = Math.round((camera.aspect || 1.78) * 20);
      const tr = sh.tracks?.[pick.song];
      const songStart = tr ? sh.t0 + (tr.at || 0) : 0;
      const playing = !!tr && t >= songStart && t < songStart + (tr.durMs || 0);

      // THE MUSIC'S OWN CUTS once the song has been measured (city/songAnalysis →
      // beatGrid → stageCuts): live cameras on its beats and its drums, timed to
      // the song as it is HEARD here (city/stageBridge), the clock until it
      // sounds. The next song is measured while this one plays.
      const measured = tr?.url ? songCuts(tr.url, tr.trackId, tr.feel) : null;
      const nextTrack = sh.tracks?.[pick.song + 1];
      if (nextTrack?.url) songCuts(nextTrack.url, nextTrack.trackId, nextTrack.feel);
      const songKey = `${sh.a}:${sh.t0}:${pick.song}`;
      const heard = playing ? (songPosition(songKey) ?? (t - songStart) / 1000) : null;

      let shot; let label = pick.kind;
      if (measured && P && heard != null) {
        const { cuts } = measured.plan; const A = measured.A;
        const ci = Math.max(0, lastAtOrBefore(cuts, heard, (c) => c.t));
        const cut = cuts[ci]; const next = cuts[ci + 1]; const before = cuts[ci - 1];
        const key = `${songKey}:${ci}`;
        if (key !== sc.shotKey || !sc.plan || sc.aspectKey !== aspectKey || sc.map !== roadMapBridge.map) {
          sc.shotKey = key;
          sc.plan = planStageShot(cut.kind, r.stage, P, sc.side, camera.aspect, cut.turn, { framing: cut.framing });
          sc.aspectKey = aspectKey;
          sc.map = roadMapBridge.map;
        }
        const u = heard - cut.t;                       // seconds into this shot
        const base = shotAt(sc.plan, u * 1000, t / 1000);
        let { pos, look, fov } = base;
        label = `${cut.cam}${cut.framing ? `:${cut.framing}` : ''}${cut.whip ? ' whip' : ''}${cut.snap ? ' crash' : ''}${cut.push ? ' push' : ''}`;
        // CRASH ZOOM: slammed in over half a beat — the jib onto their face, or a
        // pit lens from twice as wide down to its framing.
        if (cut.snap) {
          const k = easeOutExpo(u / (A.beat * 0.5));
          if (cut.kind === 'jib') {
            const fx = [P.x, P.head - 0.04, P.z];
            look = look.map((v, i) => v + (fx[i] - v) * k);
            fov += (12 - fov) * k;
          } else fov *= 2.2 - 1.2 * k;
        }
        // A SLOW SONG'S LIFT: a gentle push in on their face over a bar.
        if (cut.push) { const k = Math.min(1, u / A.bar); fov *= 1.3 - 0.3 * k * k * (3 - 2 * k); }
        // THE OPERATORS FEEL THE KICK: a handheld camera jolts with it and the
        // lens punches in on the snare — harder the harder the music drives.
        if (cut.jolt && HANDHELD.has(cut.kind)) {
          const amt = cut.band === 2 ? 1 : 0.5;
          const kick = hitEnv(A.hits?.kick, heard) * amt;
          if (cut.kind === 'pit') {
            // On a long lens a jolt is a DIP (moving toward them changes nothing you can see).
            look = [look[0], look[1] - kick * fov * 0.0012, look[2]];
          } else {
            const kj = kick * 0.014;
            const dx = look[0] - pos[0]; const dy = look[1] - pos[1]; const dz = look[2] - pos[2]; const L = Math.hypot(dx, dy, dz) || 1;
            pos = [pos[0] + (dx / L) * kj, pos[1] + (dy / L) * kj - kj * 0.3, pos[2] + (dz / L) * kj];
          }
          fov *= 1 - 0.05 * hitEnv(A.hits?.snare, heard, 0.07) * amt;   // a push-in on the snare, in proportion
        }
        // WHIP PAN: the outgoing shot flings sideways into the cut and the next
        // one arrives out of the blur, turning the same way, and settles.
        let yaw = 0;
        if (next?.whip && heard > next.t - WHIP_S && next.t - cut.t > 2 * WHIP_S) {
          const w = (heard - (next.t - WHIP_S)) / WHIP_S;
          yaw = next.turn * WHIP * w * w;
          blurWant.current = BLUR * Math.sin((Math.PI / 2) * w);
        } else if (cut.whip && u < WHIP_S && (!before || cut.t - before.t > 2 * WHIP_S)) {
          const w = u / WHIP_S;
          yaw = -cut.turn * WHIP * (1 - w) * (1 - w);
          blurWant.current = BLUR * Math.cos((Math.PI / 2) * w);
        }
        if (yaw) {
          const dx = look[0] - pos[0]; const dz = look[2] - pos[2];
          const c = Math.cos(yaw); const sn = Math.sin(yaw);
          look = [pos[0] + dx * c + dz * sn, look[1], pos[2] - dx * sn + dz * c];
        }
        shot = { pos, look, fov };
      } else {
        // Not measured (yet, or it could not be), nobody up there, or between
        // songs: the steady cutting (city/sceneShots stageShotFor).
        if (pick.key !== sc.shotKey || !sc.plan || sc.aspectKey !== aspectKey || sc.map !== roadMapBridge.map) {
          sc.shotKey = pick.key;
          sc.plan = planStageShot(pick.kind, r.stage, P, sc.side, camera.aspect, pick.turn);
          sc.aspectKey = aspectKey;
          sc.map = roadMapBridge.map;
        }
        shot = shotAt(sc.plan, Math.max(0, t - pick.start), t / 1000);
      }
      camera.position.set(shot.pos[0], shot.pos[1], shot.pos[2]);
      tmp.look.set(shot.look[0], shot.look[1], shot.look[2]);
      camera.lookAt(tmp.look);
      if (camera.isPerspectiveCamera && Math.abs(camera.fov - shot.fov) > 0.01) { camera.fov = shot.fov; camera.updateProjectionMatrix(); }
      glideIn();
      // The caption bar says what is playing (or how long until they start).
      // …or what they are saying into the mic (their opening, between songs, closing).
      const words = micWordsAt(sh, t);
      const cap = words ? { name: sh.name, text: words.text }
        : t < sh.t0 ? { name: sh.name, text: `on in ${Math.max(1, Math.ceil((sh.t0 - t) / 1000))}s` }
        : playing && tr.title ? { name: sh.name, text: `“${tr.title}”` } : null;
      if ((ct.caption?.text || null) !== (cap?.text || null)) ct.setCaption(cap);
      if (import.meta.env.DEV) cutsceneBridge.shot = { kind: `stage:${label}`, measured: !!measured, heard, pos: shot.pos, look: shot.look, fov: shot.fov, aspect: camera.aspect, people: P ? [{ id: P.id, x: P.x, z: P.z, head: P.head }] : [] };
      return;
    }

    // A scene: it has the camera.
    if (!saved.current) {
      saved.current = { pos: camera.position.clone(), quat: camera.quaternion.clone(), fov: camera.fov };
      cutsceneBridge.active = true;
      cutsceneBridge.at = saved.current.pos;
      if (cs.replay) glide.current = { ...saved.current, at: performance.now() };
    }
    cutsceneBridge.scene = r.scene || null;
    if (ct.phase !== 'cut') ct.setPhase('cut');

    const P = r.people.map((p) => person(p.id, p.st));
    const alone = P.length < 2;
    if (!scene.current || scene.current.key !== r.key) {
      scene.current = { key: r.key, startAt: t, side: alone ? 1 : chooseSide(P[0], P[1]), shotKey: 'establish', shotAt: t };
    }
    const sc = scene.current;

    // The line being said now (or the last one, in a pause).
    let line = null;
    for (const l of r.lines) {
      if (!l.playAt || l.playAt > t) continue;
      if (!line || l.playAt > line.playAt) line = l;
    }
    const sayingNow = line && t < line.playAt + (line.dur || 3) * 1000;
    const cap = sayingNow ? { name: r.names[line.speaker] || '', text: line.text } : null;
    if ((ct.caption?.text || null) !== (cap?.text || null)) ct.setCaption(cap);

    let kind = 'establish'; let S = P[0]; let L = P[1] || null; let key = 'establish'; let turn = 1;
    if (t - sc.startAt >= ESTABLISH_MS && line) {
      const half = sayingNow && (line.dur || 0) > 6 && t > line.playAt + (line.dur * 1000) / 2;
      kind = shotKindFor(line.idx ?? 0, alone, half);
      S = P.find((p) => p.id === line.speaker) || P[0];
      L = alone ? null : (P.find((p) => p !== S) || null);
      key = `${line.idx}:${kind}`;
      // Which way the move swings, alternating shot to shot (deterministic).
      turn = ((line.idx ?? 0) + (half ? 1 : 0)) % 2 ? -1 : 1;
    }
    // The move is planned once, when the shot starts (and again if the screen
    // changes shape, or a new street map arrives — a plan made before the map
    // knew no walls); every frame after plays it out.
    const aspectKey = Math.round((camera.aspect || 1.78) * 20);
    if (key !== sc.shotKey || !sc.plan || sc.aspectKey !== aspectKey || sc.map !== roadMapBridge.map) {
      if (key !== sc.shotKey) { sc.shotKey = key; sc.shotAt = t; }
      // THE SAME SIDE OF THE LINE WHOEVER SPEAKS (2026-10-30): `side` was chosen
      // from P[0] → P[1], and planShot reads it from speaker → listener — so
      // when the second of them spoke, every shot crossed the line, and at a
      // narrow pavement that side is the building (Khari: "one of the camera
      // angles is pointing directly at a wall, no artists in the view").
      sc.plan = planShot(kind, S, L, S === P[0] ? sc.side : -sc.side, camera.aspect, turn);
      sc.aspectKey = aspectKey;
      sc.map = roadMapBridge.map;
    }
    const shot = shotAt(sc.plan, t - sc.shotAt, t / 1000);
    camera.position.set(shot.pos[0], shot.pos[1], shot.pos[2]);
    tmp.look.set(shot.look[0], shot.look[1], shot.look[2]);
    camera.lookAt(tmp.look);
    if (camera.isPerspectiveCamera && Math.abs(camera.fov - shot.fov) > 0.01) { camera.fov = shot.fov; camera.updateProjectionMatrix(); }
    glideIn();
    // Probes only: what this frame's shot is, and who it is of.
    if (import.meta.env.DEV) cutsceneBridge.shot = { kind, pos: shot.pos, look: shot.look, fov: shot.fov, aspect: camera.aspect, people: P.map((p) => ({ id: p.id, x: p.x, z: p.z, head: p.head })) };
  });
  // After the director: the blur this frame's shot asked for (0 unless a whip pan is mid-swing).
  useFrame(() => setBlur(blurWant.current));

  return null;
}
